import { Component, EventEmitter, Input, OnInit, Output, OnDestroy } from '@angular/core';
import { Store, Select } from '@ngxs/store';
import { Observable, Subject } from 'rxjs';
import { AnalyticsService } from 'smartauction-frontend-micro-library';
import {
  DealerQuestionDto,
  UserQuestionDto,
  SellErrors,
  AuctionItemState,
  GetAuctionItem,
  AnswerQuestion,
  QuestionsState
} from 'remarketing-angular-library';
import { takeUntil, skip, take, takeWhile } from 'rxjs/operators';
import { get } from 'lodash';
import { ActivatedRoute } from '@angular/router';
import { InventoryItemDataService } from './../../../sell-shared/services/inventory-item-data.service';
import { SellWarnings } from '../../../../helpers/sell-warnings';
import { environment } from '../../../../../environments/environment';
import { ReplyComponent } from './reply/reply.component';
import { ViewHistoryComponent } from './view-history/view-history.component';
import { TrapFocusComponent } from 'remarketing-ui-components-lib';

import { TranslocoDirective } from '@jsverse/transloco';

@Component({
    selector: 'lib-question-drawer',
    templateUrl: './question-drawer.component.html',
    styleUrls: ['./question-drawer.component.scss'],
    imports: [TranslocoDirective, TrapFocusComponent, ViewHistoryComponent, ReplyComponent]
})
export class QuestionDrawerComponent implements OnInit, OnDestroy {
  @Input() dealer: DealerQuestionDto;
  @Output() toggleActiveQuestion = new EventEmitter<boolean>();
  @Input() open: boolean;
  @Input() questionIndex: string;
  @Output() decrementOpenQuestion = new EventEmitter();
  @Input() userUuid: string;
  @Select(AuctionItemState.auctionItemUuid) auctionItemUuid$: Observable<string>;
  @Select(AuctionItemState.auctionItemStatus) auctionItemStatus$: Observable<string>;


  question: UserQuestionDto;
  selectedQuestion$: Observable<UserQuestionDto>;
  questionError: string;
  auctionItemUuid: string;
  focusTargetOnClose: HTMLButtonElement;
  auctionItemStatus: string;

  private _activeAction: string;
  private readonly REPLY_TO_QUESTION = 'Reply to Question';

  destroyed$ = new Subject();

  constructor(
    private store: Store,
    private analyticsService: AnalyticsService,
    private route: ActivatedRoute,
    private inventoryItemDataService: InventoryItemDataService
  ) {}

  get status(): string {
    return this.question && this.question.questions
      ? this.question.questions[0].answer || this.question.questions[0].withdrawnDate
        ? 'answered'
        : 'unanswered'
      : '';
  }

  set activeAction(action: string) {
    if (typeof action === 'undefined' && typeof this.focusTargetOnClose !== 'undefined') {
      // We're closing the modal - focus on the current target
      this.focusTargetOnClose.focus();
      this.focusTargetOnClose = undefined;
    }
    this._activeAction = action;
  }

  get activeAction() {
    return this._activeAction;
  }

  ngOnInit() {
    this.selectedQuestion$ = this.store.select(QuestionsState.dealerSelectedQuestion(this.dealer.dealerUuid));
    this.selectedQuestion$.subscribe(question => (this.question = question));

    this.auctionItemUuid$
      .pipe(takeUntil(this.destroyed$))
      .subscribe(auctionItemUuid => (this.auctionItemUuid = auctionItemUuid));

    this.auctionItemStatus$
      .pipe(takeUntil(this.destroyed$))
      .subscribe(auctionItemStatus => (this.auctionItemStatus = auctionItemStatus));
  }

  toggle() {
    this.open = !this.open;
    this.toggleActiveQuestion.emit(this.open);
  }

  handleAnswer(answer: string) {
    const inventoryItemUuid = get(this.route, 'snapshot.parent.params.inventoryItemUuid', null);
    this.store.dispatch(
      new AnswerQuestion(
        this.dealer.dealerUuid,
        this.question.questions[0].questionUuid,
        this.question.userUuid,
        answer
      )
    );

    this.store
      .select(state => state)
      .pipe(
        skip(1),
        take(1),
        takeUntil(this.destroyed$)
      )
      .subscribe(state => {
        const hasError = state.sell.auctionItem.questions.questionError;

        if (hasError) {
          if (hasError.error) {
            if (hasError.error.errors[0].code === 'F-REMPLT-IIS-VEHICLENOTACTIVE') {
              this.store.dispatch(new GetAuctionItem(this.auctionItemUuid));
              this.globalErrorHandler(SellWarnings.F_REMPLT_AIS_NAC);
              this.toggle();
            } else {
              this.questionError = SellErrors.getErrorMessage(hasError.error.errors[0].code).message;
            }
          }

          this.handleAnalyticsErrorPayload(
            {
              formValues: {
                name: this.REPLY_TO_QUESTION,
                status: 'error',
                errors: [state.sell.auctionItem.questions.questionError.error.message],
                type: '',
                step: '1',
                stepname: '',
                items: [
                  {
                    allow: 'yes',
                    error: null,
                    name: 'Vehicle UUID',
                    track: 'yes',
                    value: inventoryItemUuid
                  },
                  {
                    allow: 'yes',
                    error: null,
                    name: this.REPLY_TO_QUESTION,
                    track: 'no',
                    value: answer
                  }
                ]
              }
            },
            hasError.error.errors[0].detail || 'Question Response Error',
            hasError.error.errors[0].code
          );

        } else {
          this.toggle();
          const questionBadge = document.querySelector('.openQuestionCount');
          const newValue = parseInt(questionBadge.textContent) - 1;
          newValue === 0 ? (questionBadge.innerHTML = '') : (questionBadge.innerHTML = newValue.toString());

          this.handleAnalyticsSuccessPayload({
            formValues: {
              name: this.REPLY_TO_QUESTION,
              status: 'success',
              errors: null,
              type: '',
              step: '1',
              stepname: '',
              items: [
                {
                  allow: 'yes',
                  error: null,
                  name: 'Vehicle UUID',
                  track: 'yes',
                  value: inventoryItemUuid
                },
                {
                  allow: 'yes',
                  error: null,
                  name: this.REPLY_TO_QUESTION,
                  track: 'no',
                  value: answer
                }
              ]
            }
          });
        }
      });
  }

  handleAnalyticsSuccessPayload(payload) {

      this.analyticsService.track(
        'customEvent',
        {
          name: this.REPLY_TO_QUESTION,
          attr: payload
        }
      );

  }

  handleAnalyticsErrorPayload(payload, errorMessage, errorCode) {

      this.analyticsService.track(
        'customError',
        errorMessage,
        errorCode,
        payload
      );

  }

  globalErrorHandler(errorMessage: string) {
    this.activeAction = undefined;

    this.inventoryItemDataService._globalErrorState.next({ message: errorMessage, showErrorBanner: true });
    this.inventoryItemDataService._globalOfferError.next(true);

    this.inventoryItemDataService.fetchBasicDetails();
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }
}
